import { marked } from "marked";

function headingSlug(value: string): string {
  const slug = value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/&amp;/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug || "section";
}

function decodeHeadingText(html: string): string {
  return html
    .replace(/<[^>]+>/g, "")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
}

/** Trusted repository Markdown. Articles are authored in this repo, not by visitors. */
export function renderMarkdown(source: string): string {
  const html = marked.parse(source, { async: false, gfm: true });
  const seen = new Map<string, number>();
  const withHeadingLinks = html.replace(
    /<h([23])>([\s\S]*?)<\/h\1>/g,
    (_match, level: string, inner: string) => {
      const base = headingSlug(decodeHeadingText(inner));
      const count = seen.get(base) ?? 0;
      seen.set(base, count + 1);
      const id = count === 0 ? base : `${base}-${count + 1}`;
      return `<h${level} id="${id}"><a href="#${id}">${inner}</a></h${level}>`;
    },
  );
  const withFigureLinks = withHeadingLinks.replace(
    /<figure\b([^>]*)>([\s\S]*?)<\/figure>/g,
    (match, attrs: string, inner: string) => {
      const id = attrs.match(/\sid="([^"]+)"/)?.[1];
      if (!id || inner.includes("<figcaption><a ")) {
        return match;
      }
      const linked = inner.replace(
        /<figcaption>([\s\S]*?)<\/figcaption>/,
        (_caption, text: string) => `<figcaption><a href="#${id}">${text}</a></figcaption>`,
      );
      return `<figure${attrs}>${linked}</figure>`;
    },
  );
  return withExpandButtons(withFigureLinks).replace(/<\/noscript/gi, "<\\/noscript");
}

function escapeAttribute(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/** A control on every article figure. BlogPost opens the matching image. */
function withExpandButtons(html: string): string {
  return html.replace(/<figure\b([^>]*)>([\s\S]*?)<\/figure>/g, (match, attrs: string, inner: string) => {
    if (!/<img\b/i.test(inner) || inner.includes("data-figure-expand")) {
      return match;
    }
    const captionHtml = inner.match(/<figcaption>([\s\S]*?)<\/figcaption>/)?.[1] ?? "figure";
    const label = decodeHeadingText(captionHtml).replace(/\s+/g, " ").trim() || "figure";
    const button = `<button type="button" class="figure-expand" data-figure-expand aria-label="${escapeAttribute(`Expand ${label}`)}"><svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M2.5 6V2.5H6M10 2.5h3.5V6M13.5 10v3.5H10M6 13.5H2.5V10" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg></button>`;
    const withCaptionButton = /<figcaption>[\s\S]*?<\/figcaption>/.test(inner)
      ? inner.replace(/<\/figcaption>/, `${button}</figcaption>`)
      : inner.replace(/(<img\b[^>]*>\s*)+/, (images) => `<div class="figure-frame">${button}${images}</div>`);
    const wrapped = /<div class="figure-frame">/.test(withCaptionButton)
      ? withCaptionButton
      : withCaptionButton.replace(/(<img\b[^>]*>\s*)+/, (images) => `<div class="figure-frame">${images}</div>`);
    return `<figure${attrs}>${wrapped}</figure>`;
  });
}
